import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Legend, Cell,
} from 'recharts';
import {
  FaUsers, FaBuilding, FaCheckCircle, FaUserTag, FaUserTie, FaMoneyBillWave, FaHome, FaChartLine, FaArrowUp, FaArrowDown,
} from 'react-icons/fa';
import { getAnalyticsDashboard, getExpandedAdminAnalytics } from '../../../api/services/analyticsService';
import { StatCard, ChartCard, SectionHeader, EmptyState, Alert } from './AdminUI';
import './Overview.css';

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const COLORS = ['#4f46e5', '#16a34a', '#d97706', '#dc2626', '#0ea5e9', '#8b5cf6', '#ec4899'];
const PIE_COLORS = ['#4f46e5', '#16a34a', '#d97706', '#dc2626', '#0ea5e9', '#8b5cf6', '#ec4899', '#14b8a6'];

const HealthRow = ({ label, value, icon, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, background: 'var(--admin-surface-2)', border: '1px solid var(--admin-border)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}1a`, color }}>{icon}</span>
      <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--admin-text)' }}>{label}</span>
    </div>
    <span style={{ fontFamily: 'var(--admin-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--admin-text)' }}>{value}</span>
  </div>
);

/**
 * Merged admin dashboard: an "Overview" tab (KPIs, pending queues, growth) and an
 * "Analytics" tab (revenue, role distribution, verification pipeline, operational health).
 * Both draw from the same two analytics endpoints, fetched once.
 */
const Overview = ({ onNavigate }) => {
  const [tab, setTab] = useState('overview'); // overview | analytics
  const [dashboard, setDashboard] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, expRes] = await Promise.allSettled([
        getAnalyticsDashboard(),
        getExpandedAdminAnalytics(),
      ]);
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value?.data || null);
      else throw dashRes.reason;
      if (expRes.status === 'fulfilled') setExpanded(expRes.value?.data || null);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const d = useMemo(() => dashboard || {}, [dashboard]);
  const e = useMemo(() => expanded || {}, [expanded]);

  const pendingTotal = safeNumber(d.kycPending) + safeNumber(d.kybPending) + safeNumber(d.roleRequestsPending);

  const growthSeries = useMemo(() => {
    const raw = e.userGrowthTrend || e.userGrowth7d || [];
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((item, i) => ({
      name: item.date || item.day || `Day ${i + 1}`,
      users: safeNumber(item.count || item.users || item.newUsers),
      total: safeNumber(item.total || item.cumulative),
    }));
  }, [e]);

  const roleData = useMemo(() => {
    const map = e.usersByRole || {};
    return Object.entries(map).map(([name, value]) => ({ name, value: safeNumber(value) }));
  }, [e]);

  const roleDataNonZero = useMemo(() => roleData.filter((x) => x.value > 0), [roleData]);

  const auditActions = useMemo(() => {
    const raw = e.auditActionsLast7d || e.auditActions7d || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item, i) => ({
      name: item.action || item.label || `Action ${i + 1}`,
      count: safeNumber(item.count || item.value),
    }));
  }, [e]);

  const revenueSeries = useMemo(() => {
    const rev = e.revenue || {};
    const monthly = rev.monthlyTrend || rev.monthly || [];
    if (Array.isArray(monthly) && monthly.length > 0) {
      return monthly.map((item, i) => ({
        name: item.month || item.label || `M${i + 1}`,
        revenue: safeNumber(item.amount || item.revenue || item.value),
      }));
    }
    return [
      { name: 'This Month', revenue: safeNumber(rev.month || rev.thisMonth) },
      { name: 'This Year', revenue: safeNumber(rev.year || rev.thisYear) },
    ].filter((x) => x.revenue > 0);
  }, [e]);

  const pipelineGrouped = useMemo(() => [
    { name: 'Approved', kyc: safeNumber(d.kycApproved), kyb: safeNumber(d.kybApproved) },
    { name: 'Pending', kyc: safeNumber(d.kycPending), kyb: safeNumber(d.kybPending) },
    { name: 'Rejected', kyc: safeNumber(d.kycRejected), kyb: safeNumber(d.kybRejected) },
  ], [d]);

  const verificationPipeline = useMemo(() => [
    { name: 'KYC Approved', count: safeNumber(d.kycApproved), fill: '#16a34a' },
    { name: 'KYC Pending', count: safeNumber(d.kycPending), fill: '#d97706' },
    { name: 'KYC Rejected', count: safeNumber(d.kycRejected), fill: '#dc2626' },
    { name: 'KYB Approved', count: safeNumber(d.kybApproved), fill: '#0ea5e9' },
    { name: 'KYB Pending', count: safeNumber(d.kybPending), fill: '#8b5cf6' },
    { name: 'KYB Rejected', count: safeNumber(d.kybRejected), fill: '#ef4444' },
  ], [d]);

  const userStats = e.userGrowth || e.users || {};
  const bookings = e.bookings || {};
  const maintenance = e.maintenance || {};

  const stats = [
    { icon: <FaUsers />, iconBg: 'var(--admin-primary-light)', label: 'Total Users', value: safeNumber(d.totalUsers), trend: e.userGrowthToday != null ? `+${e.userGrowthToday} today` : 'Registered', trendType: e.userGrowthToday > 0 ? 'up' : 'neutral' },
    { icon: <FaBuilding />, iconBg: '#dbeafe', label: 'Properties', value: safeNumber(d.totalProperties), trend: 'Managed assets', trendType: 'neutral' },
    { icon: <FaCheckCircle />, iconBg: 'var(--admin-success-light)', label: 'Active Assignments', value: safeNumber(d.activeTenantAssignments), trend: 'Tenants housed', trendType: 'neutral' },
    { icon: <FaUserTag />, iconBg: 'var(--admin-warning-light)', label: 'Pending Queue', value: pendingTotal, trend: pendingTotal > 0 ? 'Needs attention' : 'All clear', trendType: pendingTotal > 0 ? 'warning' : 'up' },
  ];

  const analyticsStats = [
    { icon: <FaUsers />, iconBg: 'var(--admin-primary-light)', label: 'Total Users', value: safeNumber(d.totalUsers), trend: `+${safeNumber(userStats.today)} today · +${safeNumber(userStats.thisWeek)} this week`, trendType: safeNumber(userStats.today) > 0 ? 'up' : 'neutral' },
    { icon: <FaUserTie />, iconBg: 'var(--admin-success-light)', label: 'Active Users', value: safeNumber(userStats.active || e.activeUsers), trend: `${safeNumber(userStats.suspended || e.suspendedUsers)} suspended`, trendType: 'neutral' },
    { icon: <FaBuilding />, iconBg: '#dbeafe', label: 'Properties', value: safeNumber(d.totalProperties), trend: `${safeNumber(e.properties?.published)} published · ${safeNumber(e.properties?.blocked)} blocked`, trendType: 'neutral' },
    { icon: <FaMoneyBillWave />, iconBg: 'var(--admin-warning-light)', label: 'Revenue (Month)', value: `KES ${safeNumber(e.revenue?.month).toLocaleString()}`, trend: `Year: KES ${safeNumber(e.revenue?.year).toLocaleString()}`, trendType: 'up' },
  ];

  const pendingActions = [
    { label: 'KYC Verification', count: safeNumber(d.kycPending), section: 'kyc', icon: '✅' },
    { label: 'KYB Verification', count: safeNumber(d.kybPending), section: 'kyb', icon: '🏢' },
    { label: 'Role Requests', count: safeNumber(d.roleRequestsPending), section: 'role-requests', icon: '👤' },
  ];

  return (
    <div className="overview-container">
      <SectionHeader
        title="Dashboard"
        subtitle="Platform-wide performance, growth, and operational health at a glance."
        action={
          <button type="button" className="admin-btn admin-btn-outline" onClick={fetchData} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      {/* Tabs */}
      <div className="overview-tabs">
        <button type="button" className={`overview-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          Overview
        </button>
        <button type="button" className={`overview-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
          Analytics
        </button>
      </div>

      {error && (
        <Alert type="error" onClose={fetchData}>
          <strong>Couldn't load analytics.</strong> {error} — <button type="button" onClick={fetchData} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>retry</button>
        </Alert>
      )}

      {/* ============ OVERVIEW TAB ============ */}
      {tab === 'overview' && (
        <>
          <div className="admin-grid admin-grid-stats" style={{ marginBottom: 24 }}>
            {stats.map((s) => (
              <StatCard key={s.label} {...s} loading={loading} />
            ))}
          </div>

          <div className="admin-grid admin-grid-2" style={{ marginBottom: 24 }}>
            {growthSeries.length > 0 ? (
              <ChartCard title="User Growth" subtitle="New registrations over the last 7 days" height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growthSeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }} />
                    <Area type="monotone" dataKey="users" stroke="#4f46e5" strokeWidth={2.5} fill="url(#colorUsers)" name="New users" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            ) : (
              <ChartCard title="User Growth" subtitle="New registrations over the last 7 days" height={280}>
                {loading ? <div className="admin-skeleton" style={{ height: '100%' }} /> : <EmptyState icon="📈" title="No growth data yet" text="7-day trend will appear here once available." />}
              </ChartCard>
            )}

            {verificationPipeline.some((v) => v.count > 0) ? (
              <ChartCard title="Verification Pipeline" subtitle="KYC & KYB submissions by status" height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={verificationPipeline} layout="vertical" margin={{ top: 0, right: 20, left: 20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={110} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }} cursor={{ fill: 'rgba(241,245,249,0.6)' }} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={18}>
                      {verificationPipeline.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            ) : (
              <ChartCard title="Verification Pipeline" subtitle="KYC & KYB submissions by status" height={280}>
                {loading ? <div className="admin-skeleton" style={{ height: '100%' }} /> : <EmptyState icon="✅" title="No verification data" text="KYC/KYB stats will appear here." />}
              </ChartCard>
            )}
          </div>

          <div className="admin-grid admin-grid-2">
            <div className="admin-card">
              <h3 className="admin-card-title">Pending Actions</h3>
              <p className="admin-card-subtitle">Queues that need your review</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pendingActions.map((a) => (
                  <div key={a.section} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 10, background: 'var(--admin-surface-2)', border: '1px solid var(--admin-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 18 }}>{a.icon}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--admin-text)' }}>{a.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{a.count} pending</div>
                      </div>
                    </div>
                    <button type="button" className="admin-btn admin-btn-outline" onClick={() => onNavigate?.(a.section)}>
                      {a.section === 'role-requests' ? 'View' : 'Review'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="admin-card">
              <h3 className="admin-card-title">Users by Role</h3>
              <p className="admin-card-subtitle">Distribution across the platform</p>
              {roleData.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {roleData.map((r, i) => {
                    const max = Math.max(...roleData.map((x) => x.value), 1);
                    const pct = Math.round((r.value / max) * 100);
                    return (
                      <div key={r.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                          <span style={{ fontWeight: 500, color: 'var(--admin-text)' }}>{r.name}</span>
                          <span style={{ color: 'var(--admin-text-muted)' }}>{r.value}</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: 'var(--admin-surface-2)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: COLORS[i % COLORS.length], transition: 'width 0.4s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : loading ? (
                <div className="admin-skeleton" style={{ height: 120 }} />
              ) : (
                <EmptyState icon="👥" title="No role data" text="Role distribution will appear here." />
              )}
            </div>
          </div>
        </>
      )}

      {/* ============ ANALYTICS TAB ============ */}
      {tab === 'analytics' && (
        <>
          <div className="admin-grid admin-grid-stats" style={{ marginBottom: 24 }}>
            {analyticsStats.map((s) => (
              <StatCard key={s.label} {...s} loading={loading} />
            ))}
          </div>

          <div className="admin-grid admin-grid-2" style={{ marginBottom: 24 }}>
            <ChartCard title="User Growth Trend" subtitle="New user registrations (last 7 days)" height={300}>
              {growthSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growthSeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }} />
                    <Area type="monotone" dataKey="users" stroke="#4f46e5" strokeWidth={2.5} fill="url(#gradUsers)" name="New users" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : loading ? <div className="admin-skeleton" style={{ height: '100%' }} /> : <EmptyState icon="📈" title="No growth data" text="7-day trend will appear here." />}
            </ChartCard>

            <ChartCard title="Revenue Trend" subtitle="Subscription & payment revenue" height={300}>
              {revenueSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueSeries} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }}
                      formatter={(v) => [`KES ${Number(v).toLocaleString()}`, 'Revenue']}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={2.5} dot={{ r: 4, fill: '#16a34a' }} name="Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              ) : loading ? <div className="admin-skeleton" style={{ height: '100%' }} /> : <EmptyState icon="💰" title="No revenue data" text="Revenue trend will appear here." />}
            </ChartCard>
          </div>

          <div className="admin-grid admin-grid-2" style={{ marginBottom: 24 }}>
            <ChartCard title="Users by Role" subtitle="Distribution across all roles" height={300}>
              {roleDataNonZero.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={roleDataNonZero} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
                      {roleDataNonZero.map((_, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : loading ? <div className="admin-skeleton" style={{ height: '100%' }} /> : <EmptyState icon="👥" title="No role data" text="Role distribution will appear here." />}
            </ChartCard>

            <ChartCard title="Verification Pipeline" subtitle="KYC vs KYB by status" height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineGrouped} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }} cursor={{ fill: 'rgba(241,245,249,0.6)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="kyc" fill="#4f46e5" radius={[6, 6, 0, 0]} name="KYC" barSize={36} />
                  <Bar dataKey="kyb" fill="#0ea5e9" radius={[6, 6, 0, 0]} name="KYB" barSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="admin-grid admin-grid-2">
            <ChartCard title="Admin Activity (Last 7 Days)" subtitle="Audit actions by type" height={280}>
              {auditActions.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={auditActions} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', fontSize: 13 }} cursor={{ fill: 'rgba(241,245,249,0.6)' }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#8b5cf6" barSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              ) : loading ? <div className="admin-skeleton" style={{ height: '100%' }} /> : <EmptyState icon="📋" title="No audit data" text="Admin activity will appear here." />}
            </ChartCard>

            <div className="admin-card">
              <h3 className="admin-card-title">Operational Health</h3>
              <p className="admin-card-subtitle">Bookings, maintenance &amp; assignments</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <HealthRow label="Active Tenant Assignments" value={safeNumber(d.activeTenantAssignments)} icon={<FaHome />} color="#4f46e5" />
                <HealthRow label="Total Units" value={safeNumber(d.totalUnits)} icon={<FaBuilding />} color="#0ea5e9" />
                <HealthRow label="Bookings (active)" value={safeNumber(bookings.active || bookings.total)} icon={<FaChartLine />} color="#16a34a" />
                <HealthRow label="Maintenance (open)" value={safeNumber(maintenance.open || maintenance.pending)} icon={<FaArrowUp />} color="#d97706" />
                <HealthRow label="Maintenance (resolved)" value={safeNumber(maintenance.resolved || maintenance.closed)} icon={<FaArrowDown />} color="#16a34a" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Overview;
